import React, { useEffect, useState } from 'react';
import { Card, Table, Button, message, Input, Row, Col, Tabs } from 'antd';
import { CopyOutlined, SearchOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { formQueryStringFromObject } from 'helpers';
import { useI18n } from 'hooks/useI18n';
import './ReferenceData.scss';

const { TabPane } = Tabs;

const ReferenceData = () => {
	const [searchText, setSearchText] = useState('');
	const [activeTab, setActiveTab] = useState('services');
	const [pageSizes, setPageSizes] = useState({
		services: 10,
		memberships: 10,
		coaches: 10,
	});
	const { t } = useI18n();

	const dispatch = useDispatch();
	const branchId = useSelector((state) => state?.globalRedux?.selectedBranch?.id);
	const services = useSelector((state) => state?.serviceRedux?.services || []);
	const memberships = useSelector((state) => state?.membershipRedux?.memberships || []);
	const coaches = useSelector((state) => state?.coachRedux?.coaches || []);

	useEffect(() => {
		// Fetch all reference data
		if (branchId) {
			fetchServices();
			fetchMemberships();
			fetchCoaches();
		}
	}, [branchId]);

	const fetchServices = () => {
		const string = formQueryStringFromObject({ branchId });
		dispatch(getApi('GET_SERVICES', `${SERVER_IP}api/services?${string}`));
	};

	const fetchMemberships = () => {
		const string = formQueryStringFromObject({ branchId });
		dispatch(getApi('GET_MEMBERSHIPS', `${SERVER_IP}api/memberships?${string}`));
	};

	const fetchCoaches = () => {
		const string = formQueryStringFromObject({ branchId });
		dispatch(getApi('GET_COACHES', `${SERVER_IP}api/coaches?${string}`));
	};

	const copyToClipboard = (text, label) => {
		navigator.clipboard.writeText(text);
		message.success(`${label} copied to clipboard!`);
	};

	const getFilteredData = (data, searchFields) => {
		if (!searchText) return data;
		return data.filter((item) =>
			searchFields.some((field) => {
				const value = field.split('.').reduce((obj, key) => obj?.[key], item);
				return value?.toString().toLowerCase().includes(searchText.toLowerCase());
			})
		);
	};

	const getPaginationConfig = (key, totalLabel, totalColor) => ({
		pageSize: pageSizes[key],
		showSizeChanger: true,
		pageSizeOptions: ['10', '20', '50', '100'],
		onShowSizeChange: (_, nextPageSize) => {
			setPageSizes((previous) => ({ ...previous, [key]: nextPageSize }));
		},
		showTotal: (total) => (
			<span style={{ fontWeight: 500, color: '#595959' }}>
				Total <strong style={{ color: totalColor }}>{total}</strong> {totalLabel}
			</span>
		),
	});

	const serviceColumns = [
		{
			title: t('bulk_upload.reference.services.service_name'),
			dataIndex: 'serviceName',
			key: 'serviceName',
			width: '60%',
			render: (text) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<div
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						}}
					/>
					<strong style={{ fontSize: 14 }}>{text}</strong>
				</div>
			),
		},
		{
			title: t('bulk_upload.reference.services.service_id'),
			dataIndex: 'id',
			key: 'id',
			width: '30%',
			render: (text) => (
				<code
					style={{
						background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
						padding: '4px 10px',
						borderRadius: '6px',
						fontSize: 13,
						fontWeight: 500,
						border: '1px solid #667eea30',
						display: 'inline-block',
					}}>
					{text}
				</code>
			),
		},
		{
			title: t('bulk_upload.reference.services.action'),
			key: 'action',
			width: '10%',
			align: 'center',
			render: (_, record) => (
				<Button
					type="primary"
					className="reference-copy-button"
					icon={<CopyOutlined />}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						copyToClipboard(record.id, 'Service ID');
					}}
					size="small"
					style={{ borderRadius: 6 }}>
					Copy
				</Button>
			),
		},
	];

	const membershipColumns = [
		{
			title: t('bulk_upload.reference.memberships.membership_name'),
			dataIndex: 'membershipName',
			key: 'membershipName',
			width: '40%',
			render: (text) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<div
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
						}}
					/>
					<strong style={{ fontSize: 14 }}>{text}</strong>
				</div>
			),
		},
		{
			title: t('bulk_upload.reference.memberships.service'),
			dataIndex: 'service',
			key: 'service',
			width: '25%',
			render: (service) => (
				<span
					style={{
						background: '#f0f5ff',
						padding: '3px 10px',
						borderRadius: 12,
						fontSize: 12,
						color: '#1890ff',
						fontWeight: 500,
					}}>
					{service?.serviceName || '-'}
				</span>
			),
		},
		{
			title: t('bulk_upload.reference.memberships.membership_id'),
			dataIndex: 'id',
			key: 'id',
			width: '25%',
			render: (text) => (
				<code
					style={{
						background: 'linear-gradient(135deg, #f093fb15 0%, #f5576c15 100%)',
						padding: '4px 10px',
						borderRadius: '6px',
						fontSize: 13,
						fontWeight: 500,
						border: '1px solid #f093fb30',
						display: 'inline-block',
					}}>
					{text}
				</code>
			),
		},
		{
			title: t('bulk_upload.reference.memberships.action'),
			key: 'action',
			width: '10%',
			align: 'center',
			render: (_, record) => (
				<Button
					type="primary"
					className="reference-copy-button"
					icon={<CopyOutlined />}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						copyToClipboard(record.id, 'Membership ID');
					}}
					size="small"
					style={{ borderRadius: 6 }}>
					Copy
				</Button>
			),
		},
	];

	const coachColumns = [
		{
			title: t('bulk_upload.reference.coaches.coach_name'),
			dataIndex: 'coachName',
			key: 'coachName',
			width: '30%',
			render: (text) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<div
						style={{
							width: 32,
							height: 32,
							borderRadius: '50%',
							background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: 'white',
							fontWeight: 'bold',
							fontSize: 14,
						}}>
						{text?.charAt(0)?.toUpperCase()}
					</div>
					<strong style={{ fontSize: 14 }}>{text}</strong>
				</div>
			),
		},
		{
			title: t('bulk_upload.reference.coaches.mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
			width: '15%',
			render: (text) => <span style={{ color: '#595959', fontSize: 13 }}>{text}</span>,
		},
		{
			title: t('bulk_upload.reference.coaches.email'),
			dataIndex: 'email',
			key: 'email',
			width: '25%',
			render: (text) => <span style={{ color: '#595959', fontSize: 13 }}>{text}</span>,
		},
		{
			title: t('bulk_upload.reference.coaches.coach_id'),
			dataIndex: 'id',
			key: 'id',
			width: '20%',
			render: (text) => (
				<code
					style={{
						background: 'linear-gradient(135deg, #4facfe15 0%, #00f2fe15 100%)',
						padding: '4px 10px',
						borderRadius: '6px',
						fontSize: 13,
						fontWeight: 500,
						border: '1px solid #4facfe30',
						display: 'inline-block',
					}}>
					{text}
				</code>
			),
		},
		{
			title: t('bulk_upload.reference.coaches.action'),
			key: 'action',
			width: '10%',
			align: 'center',
			render: (_, record) => (
				<Button
					type="primary"
					ghost
					className="reference-copy-button"
					icon={<CopyOutlined />}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						copyToClipboard(record.id, 'Coach ID');
					}}
					size="small"
					style={{ borderRadius: 6 }}>
					Copy
				</Button>
			),
		},
	];

	return (
		<div className="reference-data-wrapper">
			<Card
				title={
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						<div
							style={{
								width: 40,
								height: 40,
								borderRadius: 10,
								background: 'white',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: 20,
							}}>
							📋
						</div>
						<div>
							<div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-color)' }}>Reference Data</div>
							<div style={{ fontSize: 12, color: 'var(--text-color)', fontWeight: 400 }}>
								Service IDs, Membership IDs, Coach IDs - Copy & Paste
							</div>
						</div>
					</div>
				}
				className="reference-data-card"
				extra={
					<Input
						placeholder={`Search ${activeTab}...`}
						prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						style={{ width: 280, borderRadius: 8 }}
						allowClear
					/>
				}>
				<Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
					<TabPane
						tab={
							<span style={{ fontSize: 14, fontWeight: 500 }}>
								<span style={{ marginRight: 8 }}>🔧</span>
								Services <span style={{ color: '#1890ff' }}>({services.length})</span>
							</span>
						}
						key="services">
						<Table
							columns={serviceColumns}
							dataSource={getFilteredData(services, ['serviceName', 'id'])}
							rowKey="id"
							pagination={getPaginationConfig('services', 'services', '#1890ff')}
							size="middle"
							bordered
							className="reference-table"
						/>
					</TabPane>

					<TabPane
						tab={
							<span style={{ fontSize: 14, fontWeight: 500 }}>
								<span style={{ marginRight: 8 }}>💎</span>
								Memberships <span style={{ color: '#f5576c' }}>({memberships.length})</span>
							</span>
						}
						key="memberships">
						<Table
							columns={membershipColumns}
							dataSource={getFilteredData(memberships, ['membershipName', 'service.serviceName', 'id'])}
							rowKey="id"
							pagination={getPaginationConfig('memberships', 'memberships', '#f5576c')}
							size="middle"
							bordered
							className="reference-table"
						/>
					</TabPane>

					<TabPane
						tab={
							<span style={{ fontSize: 14, fontWeight: 500 }}>
								<span style={{ marginRight: 8 }}>👨‍🏫</span>
								Coaches <span style={{ color: '#00f2fe' }}>({coaches.length})</span>
							</span>
						}
						key="coaches">
						<Table
							columns={coachColumns}
							dataSource={getFilteredData(coaches, ['coachName', 'mobile', 'email', 'id'])}
							rowKey="id"
							pagination={getPaginationConfig('coaches', 'coaches', '#00f2fe')}
							size="middle"
							bordered
							className="reference-table"
						/>
					</TabPane>
				</Tabs>
				<Row style={{ marginTop: 20 }}>
					<Col span={24}>
						<Card
							size="small"
							style={{
								background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
								border: '1px solid #667eea30',
								borderRadius: 10,
							}}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
								<div
									style={{
										width: 36,
										height: 36,
										borderRadius: 8,
										background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: 18,
									}}>
									💡
								</div>
								<div>
									<div style={{ fontSize: 13, fontWeight: 600, color: '#262626', marginBottom: 2 }}>Quick Guide</div>
									<div style={{ fontSize: 12, color: '#595959' }}>
										Click the <strong>"Copy"</strong> button next to any ID to copy it to your clipboard, then paste it directly
										into your Excel file.
									</div>
								</div>
							</div>
						</Card>
					</Col>
				</Row>
			</Card>
		</div>
	);
};

export default ReferenceData;
